/**
 * Custom Lists API Service
 * Handles athlete custom list management (create, update, delete lists; add/remove schools)
 */

import { BACKEND_CONFIG } from '../config/backendConfig';

// ── Types ────────────────────────────────────────────────────────────────

export interface CustomList {
  id: number;
  athlete_id?: string;
  name: string;
  color: string;
  icon: string;
  sort_order: number;
  school_count: number;
  created_at: string;
  updated_at: string;
}

export interface CustomListSchool {
  school_id: number;
  name: string;
  ncaa_name?: string;
  division?: string;
  conference?: string;
  logo_url?: string;
  school_city?: string;
  school_state?: string;
  match_score_norm?: number;
  roster_status?: string;
  head_coach?: string;
  acceptance_rate_pct?: number;
  cost_per_year_k?: number;
  openings?: number;
  undergraduate_student_count?: number;
  coaching_staff?: any[];
  added_date: string;
}

export interface GetCustomListsResponse {
  success: boolean;
  data: {
    lists: CustomList[];
    totalLists: number;
  };
}

export interface CreateCustomListResponse {
  success: boolean;
  message: string;
  data: CustomList;
}

export interface UpdateCustomListResponse {
  success: boolean;
  message: string;
  data: CustomList;
}

export interface DeleteCustomListResponse {
  success: boolean;
  message: string;
  data: { id: number; name: string };
}

export interface GetCustomListSchoolsResponse {
  success: boolean;
  data: {
    list: { id: number; name: string; color: string };
    schools: CustomListSchool[];
    totalSchools: number;
  };
}

export interface AddSchoolToListResponse {
  success: boolean;
  message: string;
  data: { list_id: number; school_id: number; added_date?: string; already_existed: boolean };
}

export interface RemoveSchoolFromListResponse {
  success: boolean;
  message: string;
  data: { list_id: number; school_id: number };
}

export interface SchoolMembershipsResponse {
  success: boolean;
  data: {
    memberships: { [schoolId: number]: number[] };
  };
}

export interface AllCustomListSchoolsResponse {
  success: boolean;
  data: {
    schools: Array<{
      school_id: number;
      name: string;
      logo_url: string | null;
      division?: string;
      conference?: string;
      coaching_staff: any[];
      list_ids: number[];
    }>;
    totalSchools: number;
  };
}

// ── API Utility ──────────────────────────────────────────────────────────

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const url = `${BACKEND_CONFIG.baseURL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || error.error || `HTTP ${response.status}: ${error.message || 'Unknown error'}`);
  }

  return response.json();
}

// ── Custom Lists API ─────────────────────────────────────────────────────

export const customListsAPI = {
  /** Get all custom lists for an athlete (with school counts) */
  async getCustomLists(token: string, athleteId: string): Promise<GetCustomListsResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists`, { method: 'GET' }, token);
  },

  /** Create a new custom list */
  async createList(token: string, athleteId: string, name: string, color?: string): Promise<CreateCustomListResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists`, {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    }, token);
  },

  /** Update a custom list (rename / recolor) */
  async updateList(token: string, athleteId: string, listId: number, data: { name?: string; color?: string }): Promise<UpdateCustomListResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  },

  /** Delete a custom list */
  async deleteList(token: string, athleteId: string, listId: number): Promise<DeleteCustomListResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/${listId}`, { method: 'DELETE' }, token);
  },

  /** Get full school data for a custom list */
  async getListSchools(token: string, athleteId: string, listId: number): Promise<GetCustomListSchoolsResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/${listId}/schools`, { method: 'GET' }, token);
  },

  /** Add a school to a custom list */
  async addSchoolToList(token: string, athleteId: string, listId: number, schoolId: number): Promise<AddSchoolToListResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/${listId}/schools`, {
      method: 'POST',
      body: JSON.stringify({ school_id: schoolId }),
    }, token);
  },

  /** Remove a school from a custom list */
  async removeSchoolFromList(token: string, athleteId: string, listId: number, schoolId: number): Promise<RemoveSchoolFromListResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/${listId}/schools/${schoolId}`, { method: 'DELETE' }, token);
  },

  /** Get which custom lists each school belongs to (bulk) */
  async getSchoolMemberships(token: string, athleteId: string): Promise<SchoolMembershipsResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/school-memberships`, { method: 'GET' }, token);
  },

  /** Get ALL schools across ALL custom lists (for bulk messaging) */
  async getAllCustomListSchools(token: string, athleteId: string): Promise<AllCustomListSchoolsResponse> {
    return apiCall(`/athletes/${athleteId}/custom-lists/all-schools`, { method: 'GET' }, token);
  },
};
